import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Navbar from "../../Navbar";
import { UrlPortalProveedores } from "../../../services/apirest";
import {
  guardarPlantillaCorreo,
  listarPlantillasCorreo,
} from "./plantillasCorreo.service";

const ETIQUETAS = {
  alta: "Alta",
  documentacion_pendiente: "Doc. Pendiente",
  rechazo: "Rechazo",
  reactivacion: "Reactivación",
  baja: "Baja",
};

const EJEMPLO_RAZON = "Ejemplo S.A.";
const EJEMPLO_CUIT = "30-12345678-9";

function BloqueFijo({ titulo, children }) {
  return (
    <Box
      sx={{
        bgcolor: "grey.100",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
        px: 2,
        py: 1.5
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {titulo}
      </Typography>
      <Box sx={{ color: "text.secondary", "& .MuiTypography-root": { color: "text.secondary" } }}>
        {children}
      </Box>
    </Box>
  );
}

function htmlVista(actual, urlPortal) {
  const datos = `<p><strong>Razón social:</strong> ${EJEMPLO_RAZON}</p><p><strong>CUIT:</strong> ${EJEMPLO_CUIT}</p>`;
  const link =
    actual?.codigo === "alta"
      ? `<p><a href="${urlPortal}/cambiar-contrasena?token=ejemplo">Haga clic aquí para definir su contraseña</a></p><p>En su primer ingreso, deberá cambiar la contraseña para continuar.</p>`
      : actual?.codigo === "baja"
        ? `<p>Su acceso al portal <a href="${urlPortal}">${urlPortal}</a> quedó suspendido.</p>`
        : actual?.codigo === "reactivacion"
          ? `<p><strong>Contraseña:</strong> 30123456789ABC</p><p>Puede acceder al portal en: <a href="${urlPortal}">${urlPortal}</a></p>`
          : `<p>Puede acceder al portal en: <a href="${urlPortal}">${urlPortal}</a></p>`;
  const motivo =
    actual?.codigo === "rechazo"
      ? "<p><strong>Motivo:</strong> se completa al rechazar</p>"
      : "";
  return `${actual?.texto_arriba || ""}${datos}${link}${motivo}${actual?.texto_abajo || ""}`;
}

function VistaLink({ codigo, urlPortal }) {
  if (codigo === "alta") {
    return (
      <>
        <Typography variant="body2">
          Haga clic aquí para definir su contraseña
        </Typography>
        <Typography variant="body2">
          En su primer ingreso, deberá cambiar la contraseña para continuar.
        </Typography>
      </>
    );
  }

  if (codigo === "baja") {
    return (
      <Typography variant="body2">
        Su acceso al portal {urlPortal} quedó suspendido.
      </Typography>
    );
  }

  return (
    <Typography variant="body2">
      Puede acceder al portal en: {urlPortal}
    </Typography>
  );
}

function ContenidoBloque({ bloque, codigo, urlPortal }) {
  if (bloque.id === "datos_proveedor") {
    return (
      <>
        <Typography variant="body2">
          <strong>Razón social:</strong> Ejemplo S.A.
        </Typography>
        <Typography variant="body2">
          <strong>CUIT:</strong> 30-12345678-9
        </Typography>
      </>
    );
  }
  if (bloque.id === "link") {
    return <VistaLink codigo={codigo} urlPortal={urlPortal} />;
  }
  if (bloque.id === "motivo") {
    return (
      <Typography variant="body2">
        <strong>Motivo:</strong> se completa al rechazar
      </Typography>
    );
  }
  return <Typography variant="body2">{bloque.detalle}</Typography>;
}

function BotonEditor({ editor, activo, onClick, disabled, label, children }) {
  return (
    <IconButton
      size="small"
      aria-label={label}
      disabled={disabled || !editor}
      onClick={onClick}
      color={activo ? "primary" : "default"}
    >
      {children}
    </IconButton>
  );
}

function EditorTexto({ valor, onChange, disabled, etiqueta, ayuda }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: valor || "",
    editable: !disabled,
    onUpdate: ({ editor: instancia }) => {
      const html = instancia.getHTML();
      if (html.length > 8000) return;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const aplicarEnlace = () => {
    if (!editor) return;
    const previo = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Dirección del enlace", previo);
    if (url === null) return;
    if (!String(url).trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: String(url).trim() }).run();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {etiqueta}
      </Typography>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          "& .ProseMirror": { minHeight: 140, outline: "none", px: 1.5, py: 1 },
          "& .ProseMirror p": { margin: "0 0 0.5em" },
          "& .ProseMirror ul, & .ProseMirror ol": { pl: 3, my: 0.5 },
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5, px: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <BotonEditor
            editor={editor}
            label="Negrita"
            disabled={disabled}
            activo={editor?.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Cursiva"
            disabled={disabled}
            activo={editor?.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Subrayado"
            disabled={disabled}
            activo={editor?.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Lista"
            disabled={disabled}
            activo={editor?.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Lista numerada"
            disabled={disabled}
            activo={editor?.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumberedIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Enlace"
            disabled={disabled}
            activo={editor?.isActive("link")}
            onClick={aplicarEnlace}
          >
            <LinkIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Deshacer"
            disabled={disabled}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <UndoIcon fontSize="small" />
          </BotonEditor>
          <BotonEditor
            editor={editor}
            label="Rehacer"
            disabled={disabled}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <RedoIcon fontSize="small" />
          </BotonEditor>
        </Box>
        <EditorContent editor={editor} />
      </Box>
      {ayuda ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {ayuda}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function CorreosAltaProveedores() {
  const [plantillas, setPlantillas] = useState([]);
  const [tab, setTab] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");
  const [mensaje, setMensaje] = useState(null);
  const [base, setBase] = useState([]);
  const [versionEditor, setVersionEditor] = useState(0);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setErrorCarga("");
    listarPlantillasCorreo()
      .then((data) => {
        if (!cancelado) {
          setPlantillas(data);
          setBase(data);
        }
      })
      .catch((error) => {
        if (!cancelado) {
          setErrorCarga(error.message || "No se pudieron cargar las plantillas");
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const actual = plantillas[tab];
  const bloques =
    Array.isArray(actual?.bloques_fijos) && actual.bloques_fijos.length
      ? actual.bloques_fijos
      : [
          {
            id: "datos_proveedor",
            label: "Datos del proveedor",
            detalle: "Razón social y CUIT",
          },
          {
            id: "link",
            label: "Link",
            detalle:
              actual?.codigo === "alta"
                ? "Link para definir la contraseña"
                : actual?.codigo === "baja"
                  ? "Aviso de acceso suspendido"
                  : "URL del portal",
          },
          ...(actual?.codigo === "rechazo"
            ? [{ id: "motivo", label: "Motivo", detalle: "Se completa al rechazar" }]
            : []),
        ];

  const actualizar = (campo, valor) => {
    setMensaje(null);
    setPlantillas((prev) =>
      prev.map((item, index) =>
        index === tab ? { ...item, [campo]: valor } : item
      )
    );
  };

  const guardar = async () => {
    if (!actual) return;
    const asunto = String(actual.asunto || "").trim();
    if (!asunto) {
      setMensaje({ tipo: "error", texto: "El asunto es obligatorio" });
      return;
    }
    setGuardando(true);
    setMensaje(null);
    try {
      const guardada = await guardarPlantillaCorreo(actual.codigo, {
        asunto,
        texto_arriba: actual.texto_arriba || "",
        texto_abajo: actual.texto_abajo || "",
      });
      setPlantillas((prev) =>
        prev.map((item) => (item.codigo === guardada.codigo ? guardada : item))
      );
      setBase((prev) =>
        prev.map((item) => (item.codigo === guardada.codigo ? guardada : item))
      );
      setMensaje({ tipo: "success", texto: "Plantilla guardada" });
    } catch (error) {
      setMensaje({
        tipo: "error",
        texto: error.message || "No se pudo guardar la plantilla",
      });
    } finally {
      setGuardando(false);
    }
  };

  const restablecer = () => {
    const original = base.find((item) => item.codigo === actual?.codigo);
    if (!original) return;
    setPlantillas((prev) =>
      prev.map((item) => (item.codigo === original.codigo ? original : item))
    );
    setVersionEditor((valor) => valor + 1);
    setMensaje(null);
  };

  const botonGuardar = (
    <Button
      variant="contained"
      onClick={guardar}
      disabled={guardando || !actual}
      startIcon={
        guardando ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />
      }
    >
      {guardando ? "Guardando..." : "Guardar cambios"}
    </Button>
  );

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#FAFBFC" }}>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: "100px", px: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5" component="h1" fontWeight={700}>
            Configuración de plantilla de correo
          </Typography>
          {!cargando && !errorCarga ? botonGuardar : null}
        </Box>

        {cargando ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!cargando && errorCarga ? (
          <Alert severity="error">{errorCarga}</Alert>
        ) : null}

        {mensaje ? (
          <Alert severity={mensaje.tipo} sx={{ mb: 2 }}>
            {mensaje.texto}
          </Alert>
        ) : null}

        {!cargando && !errorCarga && actual ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={2}
                sx={{ p: 2, backgroundColor: "#fff", border: "1px solid #e0e0e0" }}
              >
                <Tabs
                  value={tab}
                  onChange={(_, value) => {
                    setTab(value);
                    setMensaje(null);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  {plantillas.map((item) => (
                    <Tab
                      key={item.codigo}
                      label={ETIQUETAS[item.codigo] || item.nombre || item.codigo}
                    />
                  ))}
                </Tabs>

                <TextField
                  label="Asunto del correo"
                  value={actual.asunto || ""}
                  onChange={(e) => actualizar("asunto", e.target.value)}
                  fullWidth
                  required
                  disabled={guardando}
                  inputProps={{ maxLength: 255 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => actualizar("asunto", "")}
                          disabled={guardando || !actual.asunto}
                        >
                          Limpiar
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <EditorTexto
                  key={`${actual.codigo}-arriba-${versionEditor}`}
                  etiqueta="Texto superior"
                  valor={actual.texto_arriba || ""}
                  disabled={guardando}
                  onChange={(html) => actualizar("texto_arriba", html)}
                />

                <Box display="flex" flexDirection="column" gap={1.5} sx={{ mb: 2 }}>
                  {bloques.map((bloque) => (
                    <BloqueFijo key={bloque.id} titulo={bloque.label || "Bloque"}>
                      <ContenidoBloque
                        bloque={bloque}
                        codigo={actual.codigo}
                        urlPortal={actual.url_portal || UrlPortalProveedores}
                      />
                    </BloqueFijo>
                  ))}
                </Box>

                <EditorTexto
                  key={`${actual.codigo}-abajo-${versionEditor}`}
                  etiqueta="Texto inferior"
                  valor={actual.texto_abajo || ""}
                  disabled={guardando}
                  onChange={(html) => actualizar("texto_abajo", html)}
                />

                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button variant="outlined" onClick={restablecer} disabled={guardando}>
                    Restablecer
                  </Button>
                  {botonGuardar}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  Vista previa
                </Typography>
                <Box sx={{ bgcolor: "grey.100", borderRadius: 1, p: 1.5, my: 1.5 }}>
                  <Typography variant="body2">
                    <strong>De:</strong> Italgas
                  </Typography>
                  <Typography variant="body2">
                    <strong>Asunto:</strong> {actual.asunto || "—"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    "& p": { my: 0.75 },
                    "& a": { color: "primary.main" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: htmlVista(actual, actual.url_portal || UrlPortalProveedores),
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        ) : null}
      </Container>
    </div>
  );
}
